import { Injectable, signal } from '@angular/core';

export interface ToastMessage {
  message: string;
  color: 'green' | 'red';
  id: number;
}

/**
 * Centralized toast service for the auth application
 * Manages toast notifications across all components
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private _currentToast = signal<ToastMessage | null>(null);
  private _toastTimeout: number | null = null;
  private _toastIdCounter = 0;

  /**
   * Current toast message (readonly signal)
   */
  readonly currentToast = this._currentToast.asReadonly();

  /**
   * Shows a success toast message
   * @param message - The message to display
   */
  showSuccess(message: string): void {
    this.showToast(message, 'green');
  }

  /**
   * Shows an error toast message
   * @param message - The message to display
   */
  showError(message: string): void {
    this.showToast(message, 'red');
  }

  /**
   * Shows a toast message with specified color
   * @param message - The message to display
   * @param color - The color of the toast ('green' for success, 'red' for error)
   */
  showToast(message: string, color: 'green' | 'red'): void {
    // Clear any existing timeout
    if (this._toastTimeout) {
      clearTimeout(this._toastTimeout);
      this._toastTimeout = null;
    }

    // Create new toast message
    const toastMessage: ToastMessage = {
      message,
      color,
      id: ++this._toastIdCounter,
    };

    // Set the current toast
    this._currentToast.set(toastMessage);

    // Auto-hide after 3 seconds (5 seconds for error messages)
    const duration = color === 'red' ? 5000 : 3000;
    this._toastTimeout = setTimeout(() => {
      this.hideToast();
    }, duration);
  }

  /**
   * Manually hide the current toast
   */
  hideToast(): void {
    this._currentToast.set(null);
    if (this._toastTimeout) {
      clearTimeout(this._toastTimeout);
      this._toastTimeout = null;
    }
  }

  /**
   * Clear any pending timeouts (useful for component cleanup)
   */
  clearTimeouts(): void {
    if (this._toastTimeout) {
      clearTimeout(this._toastTimeout);
      this._toastTimeout = null;
    }
  }
}
